import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Circle, ExternalLink } from "lucide-react";
import SettingsCommonLayout from "@/features/settings/SettingsCommonLayout";
import Loader from "@/shared/components/ui/Loaders/Loader";
import CommonInput from "@/shared/components/ui/Input/CommonInput";
import CustomCheckbox from "@/shared/components/ui/Checkbox/CustomCheckbox";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import ConfirmModal from "@/shared/components/ui/Modal/ConfirmModal";
import { showErrorFromMutation } from "@/shared/utils/toast";
import { searchVoiceNumbers } from "../api/voiceApi";
import {
  useAcceptVoiceAgreement,
  useBuyVoiceNumber,
  useProvisionVoiceCalling,
  useReleaseVoiceNumber,
  useRequestVoiceApproval,
  useSaveVoiceBusinessDetails,
  useSetDefaultVoiceNumber,
  useVoiceSetup,
} from "../hooks/useVoiceSetup";

const STEPS = [
  { key: "provisioning", label: "Calling account created" },
  { key: "business_details", label: "Business details added" },
  { key: "agreement", label: "Calling agreement accepted" },
  { key: "phone_number", label: "Phone number purchased" },
];

const BUSINESS_FIELDS = [
  { key: "legal_name", label: "Legal company name", required: true },
  { key: "country", label: "Country of registration", required: true },
  { key: "website", label: "Website", required: true },
  { key: "address", label: "Registered address", required: true },
  { key: "contact_name", label: "Contact name", required: true },
  { key: "contact_email", label: "Contact email", required: true, type: "email" },
  { key: "contact_phone", label: "Contact phone", required: false },
];

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });

function formatUsNumber(value) {
  const match = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(value || ""));
  return match ? `+1 (${match[1]}) ${match[2]}-${match[3]}` : value;
}

const TRANSACTION_LABELS = {
  topup: "Credit added",
  call: "Call",
  number_rental: "Number rental",
  adjustment: "Adjustment",
};

function Card({ title, description, children, action }) {
  return (
    <section className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{title}</h4>
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </section>
  );
}

function StatusBadge({ setup }) {
  const [label, tone] = setup?.suspended_at
    ? ["Suspended", "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300"]
    : setup?.ready
      ? ["Ready", "bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300"]
      : ["Setup required", "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300"];
  return <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${tone}`}>{label}</span>;
}

const APPROVAL_BADGES = {
  none: ["Not requested", "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300"],
  pending: ["Pending approval", "bg-yellow-100 text-yellow-700 dark:bg-yellow-500/10 dark:text-yellow-300"],
  rejected: ["Declined", "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300"],
};

function ApprovalGate({ approval }) {
  const { mutate: requestApproval, isPending } = useRequestVoiceApproval();
  const status = APPROVAL_BADGES[approval?.status] ? approval.status : "none";
  const [label, tone] = APPROVAL_BADGES[status];
  const isWaiting = status === "pending";

  return (
    <Card
      title="Approval required"
      description="SalesWorx must approve voice calling for your organization before you can create a calling account or buy a phone number."
      action={<span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${tone}`}>{label}</span>}
    >
      {isWaiting && approval?.requested_at && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Request sent on {dateTime.format(new Date(approval.requested_at))}. The setup options appear here once it is approved.
        </p>
      )}
      {status === "rejected" && (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Your last request was declined{approval?.note ? `: ${approval.note}` : "."} You can send a new request.
        </p>
      )}
      <div className="mt-4 flex justify-end">
        <PrimaryButton
          startContent={false}
          onClick={() => requestApproval()}
          isLoading={isPending}
          disabled={isWaiting || isPending}
        >
          {isWaiting ? "Approval requested" : "Request approval"}
        </PrimaryButton>
      </div>
    </Card>
  );
}

function BusinessDetailsForm({ business }) {
  const [form, setForm] = useState(business || {});
  const { mutate: save, isPending } = useSaveVoiceBusinessDetails();
  useEffect(() => setForm(business || {}), [business]);

  const missingRequired = BUSINESS_FIELDS.some((field) => field.required && !String(form[field.key] || "").trim());

  return (
    <form
      className="grid grid-cols-1 gap-4 md:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save(form);
      }}
    >
      {BUSINESS_FIELDS.map((field) => (
        <label key={field.key} className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
            {field.label}
            {field.required && <span className="text-red-500"> *</span>}
          </span>
          <CommonInput
            name={field.key}
            type={field.type || "text"}
            value={form[field.key] || ""}
            onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
            disabled={isPending}
          />
        </label>
      ))}
      <div className="flex justify-end md:col-span-2">
        <PrimaryButton type="submit" startContent={false} isLoading={isPending} disabled={isPending || missingRequired}>
          Save details
        </PrimaryButton>
      </div>
    </form>
  );
}

function AgreementSection({ agreement }) {
  const [checked, setChecked] = useState(false);
  const { mutate: accept, isPending } = useAcceptVoiceAgreement();
  const acceptedCurrent = Boolean(
    agreement?.accepted_at && agreement?.version && agreement.accepted_version === agreement.version,
  );

  if (!agreement?.url || !agreement?.version) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        The calling agreement is not published yet. Contact SalesWorx support.
      </p>
    );
  }

  if (acceptedCurrent) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <CheckCircle2 size={16} className="text-green-600" />
        Version {agreement.version} accepted on {dateTime.format(new Date(agreement.accepted_at))}.
        <a href={agreement.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-secondary underline">
          View <ExternalLink size={12} />
        </a>
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          <CustomCheckbox
            checked={checked}
            onChange={(event) => setChecked(event.target.checked)}
            disabled={isPending}
            ariaLabel="I accept the SalesWorx calling agreement"
          />
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          On behalf of my company, I accept the{" "}
          <a href={agreement.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-secondary underline">
            SalesWorx calling agreement <ExternalLink size={12} />
          </a>{" "}
          (version {agreement.version}). My company is the caller of record and is responsible for complying
          with TCPA, Do-Not-Call and other calling laws.
        </p>
      </div>
      <div className="flex justify-end">
        <PrimaryButton startContent={false} onClick={() => accept(agreement.version)} isLoading={isPending} disabled={!checked || isPending}>
          Accept agreement
        </PrimaryButton>
      </div>
    </div>
  );
}

function NumbersSection({ setup }) {
  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState(null);
  const { mutate: buy, isPending: isBuying, variables: buyingNumber } = useBuyVoiceNumber();
  const { mutate: makeDefault, isPending: isSettingDefault } = useSetDefaultVoiceNumber();
  const { mutate: release, isPending: isReleasing } = useReleaseVoiceNumber();
  const { mutate: search, isPending: isSearching } = useMutation({
    mutationFn: () => searchVoiceNumbers(areaCode.trim()),
    onSuccess: (response) => setResults(response?.data || []),
    onError: (error) => showErrorFromMutation(error, "Could not search numbers."),
  });
  const [pending, setPending] = useState(null);
  const provisioned = setup?.provisioning_status === "done";
  const owned = setup?.numbers || [];
  const closePending = () => setPending(null);
  const pendingLabel = pending ? formatUsNumber(pending.number.number) : "";

  return (
    <div className="flex flex-col gap-5">
      {owned.length > 0 && (
        <ul className="flex flex-col gap-2">
          {owned.map((number) => (
            <li
              key={number.number}
              className="flex flex-col gap-3 rounded-lg border border-gray-200 px-3 py-2 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700"
            >
              <div>
                <p className="flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                  {formatUsNumber(number.number)}
                  {number.is_default && (
                    <span className="rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-secondary">
                      Default
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {[number.region, `${usd.format(Number(number.monthly_rental_usd))}/month`].filter(Boolean).join(" · ")}
                </p>
              </div>
              <div className="flex gap-2">
                {!number.is_default && (
                  <SecondaryButton
                    title="Make default"
                    onClick={() => makeDefault(number.number)}
                    disabled={isSettingDefault}
                  />
                )}
                <SecondaryButton
                  title="Release"
                  onClick={() => setPending({ action: "release", number })}
                  disabled={isReleasing}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-center"
        onSubmit={(event) => {
          event.preventDefault();
          search();
        }}
      >
        <CommonInput
          value={areaCode}
          onChange={(event) => setAreaCode(event.target.value.replace(/\D/g, "").slice(0, 3))}
          placeholder="Area code, e.g. 415 (optional)"
          inputMode="numeric"
          disabled={!provisioned || isSearching}
          inputClassName="placeholder:text-xs"
        />
        <SecondaryButton
          title={isSearching ? "Searching..." : "Search US numbers"}
          onClick={() => search()}
          disabled={!provisioned || isSearching}
        />
      </form>

      {results && (
        <div className="flex flex-col gap-2">
          {results.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No numbers found. Try another area code.</p>
          ) : (
            results.map((number) => (
              <div
                key={number.number}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
              >
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100">{formatUsNumber(number.number)}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {[number.city, number.region, `${usd.format(number.monthly_rental_usd)}/month`].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <PrimaryButton
                  startContent={false}
                  disabled={isBuying}
                  isLoading={isBuying && buyingNumber === number.number}
                  onClick={() => setPending({ action: "buy", number })}
                >
                  Buy
                </PrimaryButton>
              </div>
            ))
          )}
        </div>
      )}

      {pending?.action === "buy" && (
        <ConfirmModal
          isOpen
          onClose={closePending}
          title={`Buy ${pendingLabel}?`}
          description={`It costs ${usd.format(pending.number.monthly_rental_usd)}/month. The first month is charged to your calling balance now, then every 30 days.`}
          confirmText="Buy number"
          isLoading={isBuying}
          onConfirm={() =>
            buy(pending.number.number, {
              onSuccess: () => setResults(null),
              onSettled: closePending,
            })
          }
        />
      )}

      {pending?.action === "release" && (
        <ConfirmModal
          isOpen
          destructive
          onClose={closePending}
          title={`Release ${pendingLabel}?`}
          description="It will be removed from your account and cannot be recovered. Its monthly rental stops."
          confirmText="Release number"
          isLoading={isReleasing}
          onConfirm={() => release(pending.number.number, { onSettled: closePending })}
        />
      )}
    </div>
  );
}

function WalletSection({ setup }) {
  const wallet = setup?.wallet;
  const caps = setup?.caps;
  const usage = setup?.usage;
  const stats = [
    { label: "Balance", value: usd.format(Number(wallet?.balance_usd || 0)) },
    { label: "Calls, last 24 hours", value: `${usage?.last_24h ?? 0} / ${caps?.daily_call_cap ?? "-"}` },
    { label: "Calls, last 30 days", value: `${usage?.last_30d ?? 0} / ${caps?.monthly_call_cap ?? "-"}` },
  ];
  return (
    <div className="flex flex-col gap-4">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-gray-50 px-3 py-2 dark:bg-gray-800">
            <dt className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</dt>
            <dd className="mt-0.5 text-base font-semibold tabular-nums text-gray-900 dark:text-gray-100">{stat.value}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Calls need at least {usd.format(wallet?.min_usd ?? 1)} of balance. Each number can be called at most{" "}
        {caps?.repeat_call_cap} times per {caps?.repeat_call_window_days} days, only between 8am and 9pm in the
        recipient&apos;s time zone. Contact SalesWorx to add credit.
      </p>
      {wallet?.transactions?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Date</th>
                <th className="py-1.5 pr-3 font-medium">Type</th>
                <th className="py-1.5 pr-3 font-medium">Details</th>
                <th className="py-1.5 pr-3 text-right font-medium">Amount</th>
                <th className="py-1.5 text-right font-medium">Balance</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              {wallet.transactions.map((tx) => (
                <tr key={tx.id} className="border-t border-gray-100 dark:border-gray-800">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{dateTime.format(new Date(tx.created_at))}</td>
                  <td className="py-1.5 pr-3">{TRANSACTION_LABELS[tx.type] || tx.type}</td>
                  <td className="py-1.5 pr-3">{tx.note || ""}</td>
                  <td className={`py-1.5 pr-3 text-right tabular-nums ${Number(tx.amount_usd) < 0 ? "" : "text-green-700 dark:text-green-400"}`}>
                    {usd.format(Number(tx.amount_usd))}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{usd.format(Number(tx.balance_after_usd))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function VoiceSettings({ tab }) {
  const { data: setup, isLoading } = useVoiceSetup();
  const { mutate: provision, isPending: isProvisioning } = useProvisionVoiceCalling();
  const blockers = new Set(setup?.blockers || []);
  const provisioned = setup?.provisioning_status === "done";
  const isApproved = setup?.approval?.status === "approved";
  const agreementRequired = Boolean(setup?.agreement?.required);
  const steps = STEPS.filter((step) => step.key !== "agreement" || agreementRequired);

  return (
    <SettingsCommonLayout
      title="Voice Calling"
      description="Let your team call prospects from the browser using your company's own US phone number."
      tab={tab}
    >
      {isLoading ? (
        <Loader text="Loading voice calling settings..." />
      ) : !isApproved ? (
        <div className="mt-6 flex max-w-3xl flex-col gap-6">
          <ApprovalGate approval={setup?.approval} />
        </div>
      ) : (
        <div className="mt-6 flex max-w-3xl flex-col gap-6">
          <Card
            title="Setup status"
            description={
              setup?.suspended_at
                ? `Calling is suspended: ${setup.suspended_reason || "contact SalesWorx support."}`
                : setup?.ready
                  ? "Your team can call prospects from Accounts > Relationships."
                  : "Finish these steps before your team can place calls."
            }
            action={<StatusBadge setup={setup} />}
          >
            <ul className="flex flex-col gap-2">
              {steps.map((step) => {
                const done = !blockers.has(step.key);
                return (
                  <li key={step.key} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    {done ? (
                      <CheckCircle2 size={16} className="text-green-600" />
                    ) : (
                      <Circle size={16} className="text-gray-300 dark:text-gray-600" />
                    )}
                    {step.label}
                  </li>
                );
              })}
            </ul>
            {!provisioned && (
              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {setup?.provisioning_status === "failed"
                    ? `Setup failed: ${setup.provisioning_error || "unknown error"}. Try again.`
                    : "Create the calling account for your organization to continue."}
                </p>
                <PrimaryButton startContent={false} onClick={() => provision()} isLoading={isProvisioning} disabled={isProvisioning}>
                  Set up calling
                </PrimaryButton>
              </div>
            )}
          </Card>

          <Card
            title="Business details"
            description="Used to identify your company as the caller of record, including for carrier tracebacks."
          >
            <BusinessDetailsForm business={setup?.business} />
          </Card>

          {agreementRequired && (
            <Card title="Calling agreement">
              <AgreementSection agreement={setup?.agreement} />
            </Card>
          )}

          <Card
            title="Phone numbers"
            description="Everyone in your organization calls from the default number. Only US local numbers are available."
          >
            <NumbersSection setup={setup} />
          </Card>

          <Card title="Balance and usage">
            <WalletSection setup={setup} />
          </Card>
        </div>
      )}
    </SettingsCommonLayout>
  );
}
